import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Truck, Package, Clock, CheckCircle, Warning, ArrowRight, Wrench, HouseLine, X } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import { JOSidePanel } from '../components/JOSidePanel';


const STATUS_LABELS = {
  production_completed: 'Production Completed',
  scheduled_for_dispatch: 'Scheduled',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched',
  pending_for_installation: 'Pending Installation',
  installation_completed: 'Installation Completed'
};

const STATUS_COLORS = {
  production_completed: 'bg-blue-100 text-blue-700',
  scheduled_for_dispatch: 'bg-yellow-100 text-yellow-700',
  partially_dispatched: 'bg-orange-100 text-orange-700',
  dispatched: 'bg-emerald-100 text-emerald-700',
  pending_for_installation: 'bg-indigo-100 text-indigo-700',
  installation_completed: 'bg-teal-100 text-teal-700'
};

function StatCard({ label, value, icon: Icon, color, testId, onClick, active }) {
  return (
    <Card
      data-testid={testId}
      className={`border-zinc-300 rounded-sm shadow-sm transition-all ${
        onClick ? 'cursor-pointer hover:border-orange-400 hover:shadow-md' : ''
      } ${active ? 'border-orange-500 ring-2 ring-orange-200 bg-orange-50/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="text-3xl font-light text-zinc-950 mt-1">{value}</p>
          </div>
          <Icon size={28} weight="bold" className={color} />
        </div>
      </CardContent>
    </Card>
  );
}

export function InstallationDashboard() {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sidePanelJO, setSidePanelJO] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const handleSectionClick = useCallback((section) => {
    setActiveSection(prev => {
      const isClosing = prev === section;
      if (!isClosing) {
        setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
      return isClosing ? null : section;
    });
  }, []);


  const fetchData = useCallback(async () => {
    try {
      const [statsRes, reqRes] = await Promise.all([
        api.get(`/api/installation/dashboard-stats`),
        api.get(`/api/installation-requests`)
      ]);
      setStats(statsRes.data);
      setRequests(reqRes.data.filter(r => r.status === 'pending'));
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Lazy load section data on expand
  const [sectionJobs, setSectionJobs] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);

  useEffect(() => {
    if (!activeSection) { setSectionJobs([]); return; }
    const statusMap = {
      production_completed: ['production_completed'],
      scheduled: ['scheduled_for_dispatch'],
      partially_dispatched: ['partially_dispatched'],
      dispatched: ['dispatched'],
      pending_installation: ['pending_for_installation'],
    };
    const statuses = statusMap[activeSection];
    if (!statuses) return;
    setSectionLoading(true);
    Promise.all(statuses.map(s => api.get(`/api/job-orders?status=${s}`)))
      .then(results => setSectionJobs(results.flatMap(r => r.data || [])))
      .catch(() => setSectionJobs([]))
      .finally(() => setSectionLoading(false));
  }, [activeSection]);

  // Map section jobs by expanded section for backward compat with the existing JSX
  const productionCompleted = activeSection === 'production_completed' ? sectionJobs : [];
  const scheduledJobs = activeSection === 'scheduled' ? sectionJobs : [];
  const partiallyDispatchedJobs = activeSection === 'partially_dispatched' ? sectionJobs : [];
  const dispatchedJobs = activeSection === 'dispatched' ? sectionJobs : [];
  const pendingInstallJobs = activeSection === 'pending_installation' ? sectionJobs : [];


  const handleInstallationAction = async (joNumber, action) => {
    setActionLoading(true);
    try {
      await api.post(`/api/job-orders/${joNumber}/${action}`, {});
      toast.success(action === 'mark-pending-installation' ? 'Marked as Pending Installation' : 'Installation Completed');
      fetchData();
      // Refresh current section
      setActiveSection(prev => { const s = prev; setTimeout(() => setActiveSection(s), 100); return null; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Installation Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-2">Manage dispatch and installation activities</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Ready to Dispatch" value={stats.production_completed} icon={Package} color="text-blue-600" testId="stat-prod-completed"
              onClick={() => handleSectionClick('production_completed')}
              active={activeSection === 'production_completed'}
            />
            <StatCard label="Scheduled" value={stats.scheduled_for_dispatch} icon={Clock} color="text-yellow-600" testId="stat-scheduled"
              onClick={() => handleSectionClick('scheduled_for_dispatch')}
              active={activeSection === 'scheduled_for_dispatch'}
            />
            <StatCard label="Partially Dispatched" value={stats.partially_dispatched} icon={Warning} color="text-orange-600" testId="stat-partial"
              onClick={() => handleSectionClick('partially_dispatched')}
              active={activeSection === 'partially_dispatched'}
            />
            <StatCard label="Dispatched" value={stats.dispatched} icon={CheckCircle} color="text-emerald-600" testId="stat-dispatched"
              onClick={() => handleSectionClick('dispatched')}
              active={activeSection === 'dispatched'}
            />
            <StatCard label="Pending Installation" value={stats.pending_installation} icon={Wrench} color="text-indigo-600" testId="stat-pending-install"
              onClick={() => handleSectionClick('pending_for_installation')}
              active={activeSection === 'pending_for_installation'}
            />
            <StatCard label="Installation Done" value={stats.installation_completed} icon={HouseLine} color="text-teal-600" testId="stat-install-done"
              onClick={() => handleSectionClick('installation_completed')}
              active={activeSection === 'installation_completed'}
            />
            <StatCard label="Exec Requests" value={stats.pending_requests} icon={Warning} color="text-red-600" testId="stat-requests" />
            <StatCard label="Dispatched Today" value={stats.dispatched_today} icon={Truck} color="text-purple-600" testId="stat-today" />
          </div>
        )}

        {/* Pending Executive Requests */}
        {requests.length > 0 && (
          <Card className="border-red-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-red-200 bg-red-50">
              <CardTitle className="text-xl font-bold text-red-700 flex items-center gap-2">
                <Warning size={22} weight="bold" />
                Executive Dispatch Requests ({requests.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {requests.map((req) => (
                <div key={req.requested_at} className="border border-zinc-200 rounded-sm p-4 bg-white">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-sm font-bold text-zinc-950">JO: {req.jo_numbers.join(', ')}</p>
                      <p className="text-xs text-zinc-500">By {req.requested_by_name} on {formatDate(req.requested_at)}</p>
                    </div>
                    {req.remarks && <p className="text-sm text-zinc-600 italic">"{req.remarks}"</p>}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            data-testid="nav-dispatch-mgmt"
            className="border-zinc-300 rounded-sm shadow-sm cursor-pointer hover:border-orange-400 transition-colors"
            onClick={() => navigate('/installation/dispatch')}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-zinc-950">Dispatch Management</h3>
                <p className="text-sm text-zinc-500">Schedule, allocate vehicles, create dispatch entries</p>
              </div>
              <ArrowRight size={24} className="text-zinc-400" />
            </CardContent>
          </Card>
          <Card
            data-testid="nav-dispatch-reports"
            className="border-zinc-300 rounded-sm shadow-sm cursor-pointer hover:border-orange-400 transition-colors"
            onClick={() => navigate('/installation/reports')}
          >
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-zinc-950">Dispatch Reports</h3>
                <p className="text-sm text-zinc-500">View and export dispatch data with filters</p>
              </div>
              <ArrowRight size={24} className="text-zinc-400" />
            </CardContent>
          </Card>
        </div>

        {/* Production Completed JOs Awaiting Dispatch */}
        {(!activeSection || activeSection === 'production_completed') && (
        <Card ref={sectionRef} className="border-zinc-300 rounded-sm shadow-sm">
          <CardHeader className="border-b border-zinc-300">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold text-zinc-950">Ready to Dispatch ({productionCompleted.length})</CardTitle>
              {activeSection === 'production_completed' && (
                <Button variant="ghost" size="sm" onClick={() => setActiveSection(null)} className="h-8 w-8 p-0"><X size={18} weight="bold" /></Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {sectionLoading && (
              <div className="p-8 text-center text-zinc-500">Loading...</div>
            )}
            {!sectionLoading && productionCompleted.length === 0 && (
              <div className="p-8 text-center text-zinc-500">No jobs awaiting dispatch</div>
            )}
            {!sectionLoading && productionCompleted.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionCompleted.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setSidePanelJO(jo)}>
                        <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location}</td>
                        <td className="p-3 text-zinc-700">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            data-testid={`schedule-${jo.jo_number}`}
                            onClick={() => navigate(`/installation/dispatch?jo=${jo.jo_number}`)}
                            className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8"
                          >
                            Schedule
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Scheduled for Dispatch */}
        {(!activeSection || activeSection === 'scheduled_for_dispatch') && scheduledJobs.length > 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm" data-testid="scheduled-dispatch-section">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
                <Clock size={22} weight="bold" className="text-yellow-600" />
                Scheduled for Dispatch ({scheduledJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Schedule Date</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Driver / Vehicle</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledJobs.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setSidePanelJO(jo)}>
                        <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location}</td>
                        <td className="p-3 text-yellow-700 font-bold">{formatDate(jo.dispatch_schedule_date) || jo.dispatch_schedule_date}</td>
                        <td className="p-3 text-center text-zinc-600 text-xs">{jo.scheduled_driver_name || '-'} / {jo.scheduled_vehicle_number || '-'}</td>
                        <td className="p-3 text-center text-zinc-700">{jo.no_of_windows || 0}</td>
                        <td className="p-3 text-center">
                          <Button size="sm" data-testid={`dispatch-scheduled-${jo.jo_number}`} onClick={(e) => { e.stopPropagation(); navigate(`/installation/dispatch?jo=${jo.jo_number}`); }} className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8">
                            Dispatch Now
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Partially Dispatched */}
        {(!activeSection || activeSection === 'partially_dispatched') && partiallyDispatchedJobs.length > 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm" data-testid="partial-dispatch-section">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
                <Warning size={22} weight="bold" className="text-orange-600" />
                Partially Dispatched ({partiallyDispatchedJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Total Win.</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Dispatched</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Pending</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partiallyDispatchedJobs.map(jo => {
                      const pending = (jo.no_of_windows || 0) - (jo.windows_dispatched || 0);
                      return (
                        <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setSidePanelJO(jo)}>
                          <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                          <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                          <td className="p-3 text-zinc-700">{jo.work_location}</td>
                          <td className="p-3 text-center text-zinc-700">{jo.no_of_windows || 0}</td>
                          <td className="p-3 text-center text-orange-600 font-bold">{jo.windows_dispatched || 0}</td>
                          <td className="p-3 text-center text-red-600 font-bold">{pending}</td>
                          <td className="p-3 text-center">
                            <Button size="sm" data-testid={`dispatch-partial-${jo.jo_number}`} onClick={(e) => { e.stopPropagation(); navigate(`/installation/dispatch?jo=${jo.jo_number}`); }} className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8">
                              Dispatch More
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dispatched JOs - Installation Actions */}
        {(!activeSection || activeSection === 'dispatched') && dispatchedJobs.length > 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
                <Wrench size={22} weight="bold" className="text-indigo-600" />
                Dispatched - Pending Installation ({dispatchedJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchedJobs.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setSidePanelJO(jo)}>
                        <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location}</td>
                        <td className="p-3 text-zinc-700">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
                        <td className="p-3 text-center space-x-2">
                          <Button
                            size="sm"
                            data-testid={`pending-install-${jo.jo_number}`}
                            onClick={(e) => { e.stopPropagation(); handleInstallationAction(jo.jo_number, 'mark-pending-installation'); }}
                            disabled={actionLoading}
                            className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs h-8"
                          >
                            Pending Install
                          </Button>
                          <Button
                            size="sm"
                            data-testid={`complete-install-${jo.jo_number}`}
                            onClick={(e) => { e.stopPropagation(); handleInstallationAction(jo.jo_number, 'mark-installation-completed'); }}
                            disabled={actionLoading}
                            className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                          >
                            Install Done
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending for Installation JOs */}
        {(!activeSection || activeSection === 'pending_for_installation') && pendingInstallJobs.length > 0 && (
          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardHeader className="border-b border-zinc-300">
              <CardTitle className="text-xl font-bold text-zinc-950 flex items-center gap-2">
                <HouseLine size={22} weight="bold" className="text-teal-600" />
                Pending for Installation ({pendingInstallJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInstallJobs.map(jo => (
                      <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setSidePanelJO(jo)}>
                        <td className="p-3 font-bold text-zinc-950 hover:text-orange-600">{jo.jo_number}</td>
                        <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-3 text-zinc-700">{jo.work_location}</td>
                        <td className="p-3 text-zinc-700">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            data-testid={`complete-install-pending-${jo.jo_number}`}
                            onClick={(e) => { e.stopPropagation(); handleInstallationAction(jo.jo_number, 'mark-installation-completed'); }}
                            disabled={actionLoading}
                            className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                          >
                            Mark Installation Done
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <JOSidePanel
        open={!!sidePanelJO}
        onClose={() => setSidePanelJO(null)}
        jobOrder={sidePanelJO}
      />
    </Layout>
  );
}
